// © Microsoft Corporation. All rights reserved.

import { ImageServiceConfig } from "../services/ImageServiceConfig";
import { canUseDOM } from "@msnews/isomorphic-util";
import { isInteger } from "lodash-es";

/** The cached device pixel ratio. This might not match window.devicePixelRatio as it could be bucketed into a standard size to promote easier caching. */
let cachedDevicePixelRatio: number;

/**
 * Specifies the focal region for the image.
 */
export interface ImageFocalRegion {
    /** The x coordinate of the left side of the focal region. */
    x1: number;

    /** The y coordinate of the top of the focal region. */
    y1: number;

    /** The x coordinate of the right side of the focal region. */
    x2: number;

    /** The y coordinate of the bottom of the focal region. */
    y2: number;
}

/**
 * Contains sizing information needed to generate an image URL.
 */
export interface ImageUrlGenerationInfo {
    /** The width of the image that should be generated, in device independent pixels. */
    width?: number;

    /** The height of the image that should be generated, in device independent pixels. */
    height?: number;

    /** The focal region for the image. */
    focalRegion?: ImageFocalRegion;

    /** True to enable DPI scaling and get better looking images when the screen DPI/zoom level lets us. */
    enableDpiScaling: boolean;

    /** The image resize mode. */
    mode?: ImageResizeMode;

    /** The image quality. */
    quality?: number;

    /** The background color to use when letterboxing. */
    backgroundColor?: string;
}

/**
 * Image resize mode enum
 */
export enum ImageResizeMode {
    /**
     * Default value but the Image Resizer actually replaces it by the scale option (2)
     */
    None = 0,

    /**
     * Maintains aspect ratio as letter box
     */
    Letterbox = 1,

    /**
     * Maintains aspect ratio
     */

    Scale = 2,

    /**
     * Stretches image to fit height and width
     */
    Stretch = 3,

    /**
     * Crops image to fit height and width
     */

    Crop = 4,

    /**
     * Crops the image using focal points
     */
    FocalCrop = 5,

    /**
     * Crop the image using a face auto-detection technique.
     */
    FacialCrop = 6
}

/**
 * Generates an image URL from an ID and sizing info.
 * @param id The ID of the image. e.g. "BB113a2G"
 * @param info Sizing information to generate the URL.
 * @returns The image URL.
 */
export function generateImageUrlFromId(id: string, info: ImageUrlGenerationInfo): string {
    return generateImageUrlFromBaseUrl(ImageServiceConfig.prod + id, info);
}

/**
 * Generates an image URL from a base URL.
 * @param baseUrl The base URL for the image, with no query string. e.g. "https://img-s-msn-com.akamaized.net/tenant/amp/entityid/BB12hxvq.img"
 * @param info Sizing information to generate the URL.
 * @returns The image URL.
 */
export function generateImageUrlFromBaseUrl(baseUrl: string, info: ImageUrlGenerationInfo): string {
    const { focalRegion, backgroundColor } = info;

    // Passing width or height 0 to image resizer will cause it to ignore that dimension and size on the other one
    let { width = 0, height = 0 } = info;

    let auxQsp: string = "";
    if (focalRegion) {
        let focusX = midPoint(focalRegion.x1, focalRegion.x2);
        let focusY = midPoint(focalRegion.y1, focalRegion.y2);

        if (focusX != null && focusY != null) {
            auxQsp += `&x=${focusX}&y=${focusY}`;
        }
    }

    if (backgroundColor) {
        auxQsp += `&b=${backgroundColor}`;
    }

    // m=6 is facial crop. If a focal crop area is provided, we will add the x and y parameters, which will override the facial crop and do a manual focal crop.
    const mode: number = info.mode == null ? 6 : info.mode;

    // Image resizer has a bug where upscaling is not enabled by default, so we need to force it with the query param.
    if (mode === 6) {
        auxQsp += "&u=t";
    }

    if (info.enableDpiScaling) {
        // If we have info about the device pixel ratio, convert these device independent pixels into physical pixels.
        const pixelRatio: number = getDevicePixelRatio();
        if (pixelRatio) {
            width = Math.round(width * pixelRatio);
            height = Math.round(height * pixelRatio);
        }
    }

    const quality: number = info.quality || 60;

    // Image resizer documentation: https://microsoft.sharepoint.com/teams/publishing_infrastructure/_layouts/15/Doc.aspx?sourcedoc={5a9c9f56-4762-47a6-8cd3-c0146bc9bf9a}&action=view&wd=target%28Develop.one%7C3340d642-e2bf-4bdd-9b63-42c479572962%2FImage%20Resizer%20Parameters%7Cdeb5ddfd-d569-45e1-8c21-ed41cecd8f91%2F%29
    return `${baseUrl}?w=${width}&h=${height}&q=${quality}&m=${mode}&f=jpg${auxQsp}`;
}

/**
 * Gets the device pixel ratio to use for generating image URLs.
 * @returns The device pixel ratio.
 */
function getDevicePixelRatio(): number {
    if (cachedDevicePixelRatio) {
        return cachedDevicePixelRatio;
    }

    if (!canUseDOM() || !window.devicePixelRatio || window.devicePixelRatio < 1) {
        return null;
    }

    cachedDevicePixelRatio = window.devicePixelRatio;

    // Special case the x.25 / x.5 / x.75 values, they are always OK
    if (!isInteger(cachedDevicePixelRatio * 4)) {
        // If not special cased, round up to nearest tenth.
        cachedDevicePixelRatio = Math.ceil(cachedDevicePixelRatio * 10) / 10;
    }

    // Cap at 3x
    if (cachedDevicePixelRatio > 3) {
        cachedDevicePixelRatio = 3;
    }

    return cachedDevicePixelRatio;
}

/**
 * Returns the midpoint between two min/max coordinate points.
 * @param min - the min coordinate
 * @param max - the max coordinate
 * @returns the midpoint between min and max.
 */
function midPoint(min: number, max: number): number {
    if (min != null && max != null) {
        return Math.floor((min + max) / 2);
    }
}